import {FeatureType, GetAddOnResponse, ObjectId, SubscriptionFeature} from '../../types';

import { MONETIZATION_ENABLED } from '../constants';
import { State } from './state-management/state';
import accountInfo from './account-info';
import organizationInfo from './organization-info';
import { property } from './state-management/decorators/property';
import topicData from './topic-data';

class FeaturesInfo extends State {

    @property({ type: Boolean }) is_ready: boolean = false;

    @property({ type: Boolean }) isViewer: boolean = false;

    @property({ type: Map }) enabledFeatures: Map<FeatureType, String> = new Map<
        FeatureType,
        String
    >();

    id: ObjectId | undefined = { $oid: '' };

    static amplitude_callsite: string = 'Features Info Cache';

    // create a map of feature type to feature value from organization features and account features
    // this will be used to check if a feature is enabled or not
    // priority is given to organization features since that is what we are moving to
    updateEnabledFeaturesMap() {
        const featuresMap = new Map();
        // if organization is set and they are not a guest or a proxi.co teammate, use organization features
        if (organizationInfo.id?.$oid && !organizationInfo.isGuest) {
            for (const feature of organizationInfo.enabledFeatures) {
                featuresMap.set(feature.key, feature.value);
            }
        } else {
            for (const feature of accountInfo.enabledFeatures) {
                featuresMap.set(feature.key, feature.value);
            }
        }
        this.enabledFeatures = featuresMap;
        this.is_ready = true;
    }

    setEnabledFeaturesForViewers(features: SubscriptionFeature[]) {
        const featuresMap = new Map();
        for (const feature of features) {
            featuresMap.set(feature.key, feature.value);
        }
        this.enabledFeatures = featuresMap;
        this.isViewer = true;
        this.is_ready = true;
    }

    refreshFeatures() {
        this.is_ready = false;
        this.updateEnabledFeaturesMap();
    }

    get isProxiCoTeammate(): boolean {
        return accountInfo.email.endsWith('@proxi.co');
    }


    // HELPERS
    getBooleanFeature(featureType: FeatureType): boolean {
        if (!this.is_ready) {
            this.updateEnabledFeaturesMap();
        }

        if (MONETIZATION_ENABLED) {
            const featureValue = this.enabledFeatures.get(featureType);
            return featureValue === 'true' || this.isProxiCoTeammate;
        }
        return true;
    }

    getNumberFeature(featureType: FeatureType, defaultValue: number, unlimitedValue: number): number {
        if (!this.is_ready) {
            this.updateEnabledFeaturesMap();
        }

        if (MONETIZATION_ENABLED) {
            const featureValue = this.enabledFeatures.get(featureType);
            if (featureValue === 'Unlimited' || this.isProxiCoTeammate) {
            // if (featureValue === 'Unlimited') {
                return unlimitedValue;
            } if (featureValue) {
                return parseInt(featureValue.toString());
            } else {
                return defaultValue;
            }
        }
        return defaultValue;
    }

    // GET FEATURES

    // Map management

    get publiclyViewableEnabled(): boolean {
        // publicly avaialable only appears for people who have never had a subsciption before and are likely trialing the software
        const two_weeks_ago_iso = new Date(Date.now() - 12096e5).toISOString();
        const was_made_less_than_two_weeks_ago = (topicData.topic?.created_at ?? two_weeks_ago_iso) >= two_weeks_ago_iso;
        return (was_made_less_than_two_weeks_ago || this.enabledFeatures.get(FeatureType.Backpack) === 'true');
    }

    get backpackEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.Backpack);
    }

    get creatorProfilesEnabled(): boolean {
        return true;
    }

    get foldersEnabled(): boolean {
        return true;
    }

    get qrCodesEnabled(): boolean {
        return true;
    }

    get embedLinkEnabled(): boolean {
        return true;
    }

    get collaborativeMapEnabled(): boolean {
        return true;
    }

    // Map settings
    get proxiAIEnabled(): boolean {
        return true;
    }

    get categoriesAndCustomIconsEnabled(): boolean {
        return true;
    }

    get tagsEnabled(): boolean {
        return true;
    }

    get customUrlsEnabled(): boolean {
        return true;
    }

    get headerBasicCustomizationEnabled(): boolean {
        return true;
    }

    get socialMediaImageEnabled(): boolean {
        return true;
    }

    get mapStylingEnabled(): boolean {
        return true;
    }

    get customMapStylingEnabled(): boolean {
        return true;
    }

    get upvotingEnabled(): boolean {
        return true;
    }

    get automaticallyShowPointCardListEnabled(): boolean {
        return true;
    }

    get pointNumberingEnabled(): boolean {
        return true;
    }

    get panningBetweenPointsInListEnabled(): boolean {
        return true;
    }

    get requireApprovalOnPointsEnabled(): boolean {
        return true;
    }

    get spreadsheetViewEnabled(): boolean {
        return true;
    }

    get kmlUploadEnabled(): boolean {
        return true;
    }

    get basicInsightsEnabled(): boolean {
        return true;
    }

    get pointImageUploadEnabled(): boolean {
        return true;
    }

    // Allow everyone to share for now as a test
    get shareMapEnabled(): boolean {
        return true;
    }

    // STARTER

    // Map management
    get addMapEditorsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.AddMapEditors);
    }

    get customShapesEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.CustomShapes);
    }

    get addFolderEditorsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.AddFolderEditors);
    }

    get emailCaptureEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.EmailCapture);
    }

    // Map sharing
    get printableMapEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.PrintableMap);
    }

    // Map sharing
    get makeMapsPrivateEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.MakeMapsPrivate);
    }

    // Map settings
    get headerLinkOutEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HeaderLinkOut);
    }

    get headerLogoEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HeaderLogo);
    }

    get dateTimeEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.DateTime);
    }

    get disableGoogleImagesEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.DisableGoogleImages);
    }

    get hideHeaderEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HideHeader);
    }

    get customFontEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.CustomFont);
    }

    get advancedInsightsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.AdvancedInsights);
    }

    // PRO

    // Map management
    get duplicateMapEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.DuplicateMap);
    }

    // Map sharing

    // Map settings
    get disableAlsoFeaturedOnEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.DisableAlsoFeaturedOn);
    }

    get customizeAddPointFormEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.CustomizeAddPointForm);
    }

    get hideBuildYourOwnMapEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HideBuildYourOwnMap);
    }

    get hideAllCommandCenterButtonsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HideAllCommandCenterButtons);
    }

    get zapierEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.ZapierAccess);
    }

    get passwordEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.PasswordProtection);
    }

    get paywallEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.PaywallProtection);
    }

    // temp for testing
    get emailProtectedEnabled(): boolean {
        return true;
    }

    get pointLeadGenEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.EmailCaptureOnPoint);
    }

    get customizeAboutMapEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.CustomizeAboutMapDrawer);
    }

    get hideGetRouteEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HideGetRoute);
    }

    get hideMultiSelectEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HideMultiSelect);
    }

    get hideBookmarkEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.HideBookmarkButtons);
    }

    get customFilteringOptionsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.CustomFilteringOptions);
    }

    get customZoomEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.SetCustomZoom);
    }

    // TEAMS

    // Advanced Features

    get challengePassportEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.ChallengePassport);
    }

    get guideEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.Guide);
    }

    get scavengerHuntEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.ScavengerHunt);
    }

    get electionEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.Election);
    }

    get sharedBackpackEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.TeamSharedBackpack);
    }

    get brandStandardsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.BrandStandards);
    }

    get analyticsAcrossAllMapsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.AnalyticsAcrossMaps);
    }

    get leadsAcrossAllMapsEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.LeadsAcrossMaps);
    }

    get customDomainEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.CustomDomain);
    }

    get customEmailEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.CustomEmail);
    }

    get apiAccessEnabled(): boolean {
        return this.getBooleanFeature(FeatureType.ApiAccess);
    }

    // COUNTS
    get numberOfMapsPerEmailEnabled(): number {
        return this.getNumberFeature(FeatureType.NumberOfMapsPerEmail, -1, -1);
    }

    get pointsPerMapEnabled(): number {
        const default_value = accountInfo.legacy_account === true ? 150 : 25;
        return this.getNumberFeature(FeatureType.PointsPerMap, default_value, 50000);
    }

    get translationLanguagesEnabled(): number {
        return this.getNumberFeature(FeatureType.AutotranslationLanguages, 1, 1);
    }

    get numberOfEditorsPerMapEnabled(): number {
        return this.getNumberFeature(FeatureType.NumberOfEditorsPerMap, 1, -1);
    }

    get numberOfEditorsPerFolderEnabled(): number {
        return this.getNumberFeature(FeatureType.NumberOfEditorsPerFolder, 1, -1);
    }

    get numberOfUsesOfProxiAIEnabled(): number {
        return this.getNumberFeature(FeatureType.NumberOfUsesOfProxiAI, 3, 100);
    }

    get numberOfUsesOfRoutesEnabled(): number {
        return this.getNumberFeature(FeatureType.NumberOfUsesOfRoutes, 3, 100);
    }

    get numberOfViewsPerMapEnabled(): number {
        return this.getNumberFeature(FeatureType.NumberOfViewsPerMap, 100000, 100000);
    }

    get creatorPaywallTake(): number {
        return this.getNumberFeature(FeatureType.CreatorPaywallTake, 0, 0);
    }

    // AddOn checking
    public addOnEnabled(addon: GetAddOnResponse): boolean {
        if (MONETIZATION_ENABLED && Array.isArray(addon.required_entitlements) && !this.isProxiCoTeammate) {
            for (let i = 0; i < addon.required_entitlements.length; i++) {
                const value = this.enabledFeatures.get(addon.required_entitlements[i]);
                if (value === 'true') {
                    continue;
                }
                const numValue = Number(value);
                if (!isNaN(numValue)) {
                    if (numValue === 0) {
                        return false;
                    } else if (numValue > 0) {
                        continue;
                    }
                }
                return false;
            }
        }
        return true;
    }
}

const featuresInfo = new FeaturesInfo();

export default featuresInfo;
